import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { todayISO, fmtDate, fmtNum } from "../lib/format";
import { Plus, Trash2 } from "lucide-react";
import { useAuth, hasModuleAccess } from "../lib/auth";
import ExportButtons from "../components/ExportButtons";
import FileUpload from "../components/FileUpload";
import AttachmentThumb from "../components/AttachmentThumb";
import { useI18n } from "../lib/i18n";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import AssetPurchases from "./AssetPurchases";
import { useSearchParams } from "react-router-dom";
import PurchaseDetailsDialog from "../components/PurchaseDetailsDialog";
import LinenPurchaseEditDialog from "../components/LinenPurchaseEditDialog";

export default function Purchases() {
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const canAdd = hasModuleAccess(user, "purchase_register", "manage");
  const canSeeAssets = hasModuleAccess(user, "asset_management", "view");
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({
    bill_number: "",
    po_number: "",
    supplier: "",
    bill_date: todayISO(),
    remarks: "",
    receiving_location: "STORE_CLEAN",
    attachment_url: null,
  });
  const [lines, setLines] = useState([{ item_id: "", qty: 1, rate: 0 }]);
  const [detailPurchase, setDetailPurchase] = useState(null);
  const [editPurchase, setEditPurchase] = useState(null);
  const [outerTab, setOuterTab] = useState(
    canSeeAssets && sp.get("tab") === "assets" ? "assets" : "linen",
  );

  const load = async () => {
    const [i, p] = await Promise.all([
      api.get("/items"),
      api.get("/purchases"),
    ]);
    setItems(i.data);
    setRows(p.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    const clean = lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (!f.bill_number || !f.bill_date || !clean.length) {
      return toast.error("Bill number, bill date, and items are required");
    }
    try {
      await api.post("/purchases", {
        ...f,
        reference: f.po_number || f.bill_number,
        purchase_date: f.bill_date,
        lines: clean.map((line) => ({
          item_id: line.item_id,
          qty: Number(line.qty),
          rate: line.rate === "" ? null : Number(line.rate),
        })),
      });
      toast.success("Purchase recorded — stock increased");
      setLines([{ item_id: "", qty: 1, rate: 0 }]);
      setF({
        bill_number: "",
        po_number: "",
        supplier: "",
        bill_date: todayISO(),
        remarks: "",
        receiving_location: "STORE_CLEAN",
        attachment_url: null,
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const itemName = (id) => items.find((x) => x.id === id)?.name || id;
  const totalBillValue = lines.reduce(
    (sum, line) => sum + Number(line.qty || 0) * Number(line.rate || 0),
    0,
  );

  const deleteLinenPurchase = async (purchase) => {
    if (!window.confirm(`Delete purchase ${purchase.bill_number || purchase.reference}?`)) {
      return;
    }
    try {
      await api.delete(`/purchases/${purchase.id}`);
      toast.success("Purchase deleted and receipt stock reversed");
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="section-title" data-testid="page-title">
            {t("nav.purchases")}
          </h1>
          <div className="section-sub">
            {outerTab === "assets"
              ? "Asset & consumable purchases. Only approved receipts increase stock."
              : t("sub.purchases")}
          </div>
        </div>
        {outerTab === "linen" && (
          <ExportButtons
            endpoint="/reports/export/master-stock"
            filenameBase="purchases"
          />
        )}
      </div>

      <Tabs value={outerTab} onValueChange={setOuterTab}>
        <TabsList
          className="mb-4 grid h-auto w-full grid-cols-1 sm:flex sm:w-auto"
          data-testid="pur-outer-tabs"
        >
          <TabsTrigger
            className="w-full sm:w-auto"
            value="linen"
            data-testid="pur-tab-linen"
          >
            Linen Purchases
          </TabsTrigger>
          {canSeeAssets && (
            <TabsTrigger
              className="w-full sm:w-auto"
              value="assets"
              data-testid="pur-tab-assets"
            >
              Assets &amp; Consumables Purchases
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="linen">
          {canAdd && (
            <Card className="p-4 md:p-6 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <div>
                  <Label>Bill / Invoice Number</Label>
                  <Input
                    value={f.bill_number}
                    onChange={(e) =>
                      setF({ ...f, bill_number: e.target.value })
                    }
                    className="h-11 mt-1"
                    data-testid="pur-bill-number"
                  />
                </div>
                <div>
                  <Label>Reference / PO Number</Label>
                  <Input
                    value={f.po_number}
                    onChange={(e) => setF({ ...f, po_number: e.target.value })}
                    className="h-11 mt-1"
                    data-testid="pur-po-number"
                  />
                </div>
                <div>
                  <Label>{t("col.supplier")}</Label>
                  <Input
                    value={f.supplier}
                    onChange={(e) => setF({ ...f, supplier: e.target.value })}
                    className="h-11 mt-1"
                    data-testid="pur-supplier"
                  />
                </div>
                <div>
                  <Label>Bill Date</Label>
                  <Input
                    type="date"
                    value={f.bill_date}
                    onChange={(e) => setF({ ...f, bill_date: e.target.value })}
                    className="h-11 mt-1"
                    data-testid="pur-bill-date"
                  />
                </div>
                <div>
                  <Label>{t("pur.receive_into")}</Label>
                  <Select
                    value={f.receiving_location}
                    onValueChange={(v) => setF({ ...f, receiving_location: v })}
                  >
                    <SelectTrigger
                      className="h-11 mt-1"
                      data-testid="pur-receive-into"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STORE_CLEAN">Store Clean</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-semibold text-slate-700">
                  {t("pur.line_items")}
                </div>
                {lines.map((l, i) => (
                  <div
                    key={l.item_id || `row-${i}`}
                    className="grid grid-cols-12 gap-2 items-end"
                  >
                    <div className="col-span-6">
                      <Select
                        value={l.item_id}
                        onValueChange={(v) =>
                          setLines(
                            lines.map((x, y) =>
                              y === i ? { ...x, item_id: v } : x,
                            ),
                          )
                        }
                      >
                        <SelectTrigger
                          className="h-11"
                          data-testid={`pur-item-${i}`}
                        >
                          <SelectValue placeholder="Item" />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((it) => (
                            <SelectItem key={it.id} value={it.id}>
                              {it.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={l.qty}
                        onChange={(e) =>
                          setLines(
                            lines.map((x, y) =>
                              y === i ? { ...x, qty: e.target.value } : x,
                            ),
                          )
                        }
                        className="h-11"
                        data-testid={`pur-qty-${i}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Unit Rate"
                        value={l.rate}
                        onChange={(e) =>
                          setLines(
                            lines.map((x, y) =>
                              y === i ? { ...x, rate: e.target.value } : x,
                            ),
                          )
                        }
                        className="h-11"
                        data-testid={`pur-rate-${i}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setLines(lines.filter((_, y) => y !== i))
                        }
                        data-testid={`pur-remove-line-${i}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() =>
                    setLines([...lines, { item_id: "", qty: 1, rate: 0 }])
                  }
                  data-testid="pur-add-line"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add line
                </Button>
                <div
                  className="font-semibold text-sm"
                  data-testid="pur-total-bill-value"
                >
                  Total Bill Value: {totalBillValue.toFixed(2)}
                </div>
              </div>
              <div className="mt-3">
                <Label>Remarks</Label>
                <Textarea
                  value={f.remarks}
                  onChange={(e) => setF({ ...f, remarks: e.target.value })}
                  data-testid="pur-remarks"
                />
              </div>
              <div className="mt-3">
                <Label>Attachment (invoice photo / PDF)</Label>
                <div className="mt-1">
                  <FileUpload
                    value={f.attachment_url}
                    onChange={(u) => setF({ ...f, attachment_url: u })}
                    testid="pur-attachment"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button
                  className="h-12 bg-slate-900 text-white hover:bg-slate-800 px-8"
                  onClick={submit}
                  data-testid="pur-submit"
                >
                  Record Purchase
                </Button>
              </div>
            </Card>
          )}

          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="vr-table" data-testid="purchases-table">
                <thead>
                  <tr>
                    <th>Bill Date</th>
                    <th>Bill / Invoice</th>
                    <th>Reference / PO</th>
                    <th>Supplier</th>
                    <th className="num">Items</th>
                    <th className="num">Total Qty</th>
                    <th className="num">Total Value</th>
                    <th>Attachment</th>
                    <th>By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.bill_date || r.purchase_date}</td>
                      <td className="font-mono">{r.bill_number || "—"}</td>
                      <td className="font-mono">
                        {r.po_number || r.reference || "—"}
                      </td>
                      <td>{r.supplier || "—"}</td>
                      <td className="num">{r.lines?.length}</td>
                      <td className="num">{fmtNum(r.total_qty)}</td>
                      <td className="num">{fmtNum(r.total_bill_value)}</td>
                      <td>
                        <AttachmentThumb
                          url={r.attachment_url}
                          testid={`pur-thumb-${r.reference}`}
                        />
                      </td>
                      <td>{r.submitted_by_name}</td>
                      <td>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDetailPurchase(r)}
                            data-testid={`pur-details-${r.id}`}
                          >
                            Details
                          </Button>
                          {canAdd && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditPurchase(r)}
                              data-testid={`pur-edit-${r.id}`}
                            >
                              Edit
                            </Button>
                          )}
                          {canAdd && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-700 hover:bg-red-50"
                              onClick={() => deleteLinenPurchase(r)}
                              data-testid={`pur-delete-${r.id}`}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center text-slate-500 py-5"
                      >
                        No purchases yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
        {canSeeAssets && (
          <TabsContent value="assets">
            <AssetPurchases embedded />
          </TabsContent>
        )}
      </Tabs>
      <PurchaseDetailsDialog
        open={!!detailPurchase}
        onOpenChange={(open) => !open && setDetailPurchase(null)}
        purchase={detailPurchase}
        endpoint="/purchases"
        testid="linen-purchase"
      />
      <LinenPurchaseEditDialog
        open={!!editPurchase}
        onOpenChange={(open) => !open && setEditPurchase(null)}
        purchase={editPurchase}
        items={items}
        onSaved={() => load()}
      />
    </Layout>
  );
}
